import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { getFetchError } from '@/reducers/fetch'
import {
  getAllActiveUserActionItems,
  getAllCompletedUserActionItems,
  getAllUpcomingUserActionItems,
} from '../lib/userActionItems.selectors'
import {
  FETCH_USER_ACTION_ITEMS_KEY,
  groupOptions,
} from '../lib/userActionItems.slice'
import { useReselector } from '@/utils/sharedHooks'
import {
  UserActionItemTabPane,
  ACTION_ITEM_TYPE,
} from '../UserActionItemTabPane'
import { TypographyStyles } from '@/styles'
import {
  Alert,
  Badge,
  Card,
  CardColors,
  Select,
  Tabs,
  Text,
  Loader,
} from '@/ui'

interface ToDosCardProps {
  isFetching: boolean
}

export const ToDosCard = ({ isFetching }: ToDosCardProps) => {
  const defaultSortValue = groupOptions[0].value
  const [dropdownValue, setDropdownValue] = useState(defaultSortValue)
  const [searchParams, setSearchParams] = useSearchParams()
  const defaultTab = searchParams.get('tab')
  const activeItems = useReselector(getAllActiveUserActionItems)
  const upcomingItems = useReselector(getAllUpcomingUserActionItems)
  const completedItems = useReselector(getAllCompletedUserActionItems)
  const fetchError = useReselector(getFetchError, FETCH_USER_ACTION_ITEMS_KEY)

  const [activeTab, setActiveTab] = useState(() => {
    switch (defaultTab) {
      case 'upcoming':
        return 'upcoming'
      case 'completed':
        return 'completed'
      case 'active':
      default:
        return 'active'
    }
  })

  const updateTab = (value: string) => {
    setSearchParams({ tab: value }, { replace: true })
    setActiveTab(value)
  }

  return (
    <Card
      className="min-h-[300px]"
      color={CardColors.LEVEL_0}
      id="dashboard-todos"
    >
      <div className="flex flex-col space-y-4">
        <div className="flex justify-between items-center">
          <Text variant={TypographyStyles.H1} className="w-7/16">
            To Do
          </Text>

          <div className="w-3/8 flex items-center justify-end">
            <Text className="pr-2">Sort:</Text>

            <Select
              options={groupOptions.map((option) => ({
                text: option.text,
                value: option.value,
              }))}
              value={dropdownValue}
              onValueChange={(val) => {
                if (val !== undefined) {
                  setDropdownValue(val as typeof defaultSortValue)
                }
              }}
            />
          </div>
        </div>

        {fetchError && (
          <Alert
            variant="error"
            description="There was an error fetching your To Do's. Please refresh the page to try again."
          />
        )}

        <div>
          {isFetching && <Loader active />}

          {!isFetching && !fetchError && (
            <Tabs value={activeTab} onValueChange={updateTab}>
              <Tabs.List>
                <Tabs.Trigger value="active">
                  Active
                  {Boolean(activeItems.length) && (
                    <Badge color="green" size="sm" className="ml-2">
                      {activeItems.length}
                    </Badge>
                  )}
                </Tabs.Trigger>

                <Tabs.Trigger value="upcoming">Upcoming</Tabs.Trigger>
                <Tabs.Trigger value="completed">Completed</Tabs.Trigger>
              </Tabs.List>

              <Tabs.Content value="active">
                <UserActionItemTabPane
                  groupOption={dropdownValue}
                  tabPaneType={ACTION_ITEM_TYPE.ACTIVE}
                  userActionItems={activeItems}
                />
              </Tabs.Content>

              <Tabs.Content value="upcoming">
                <UserActionItemTabPane
                  groupOption={dropdownValue}
                  tabPaneType={ACTION_ITEM_TYPE.UPCOMING}
                  userActionItems={upcomingItems}
                />
              </Tabs.Content>

              <Tabs.Content value="completed">
                <UserActionItemTabPane
                  groupOption={dropdownValue}
                  tabPaneType={ACTION_ITEM_TYPE.COMPLETED}
                  userActionItems={completedItems}
                />
              </Tabs.Content>
            </Tabs>
          )}
        </div>
      </div>
    </Card>
  )
}
